import { Injectable } from '@angular/core';
import { CommonAddressResponse } from 'src/app/core/models/CommonAddressResponse';
import { UserService } from 'src/app/core/user/user.service';
import { ChannelService } from 'src/app/home/channel/channel.service';
import { AddressService } from './address.service';

export interface LocationCoordinates {
  longitude: number;
  latitude: number;
}

export interface AutomaticLocationContext extends LocationCoordinates {
  cancellationVersion: number;
}

@Injectable({
  providedIn: 'root'
})
export class LocationCoordinatorService {

  constructor(
    private addressService: AddressService,
    private channelService: ChannelService,
    private userService: UserService
  ) { }

  async requestAutomaticLocation(): Promise<AutomaticLocationContext | null> {
    if (this.addressService.isAddressDialogOpen()) {
      return null;
    }

    const cancellationVersion = this.addressService.getAutomaticLocationCancellationVersion();
    const location = await this.addressService.initializeLocationPermission();

    if (!location || !this.addressService.canApplyAutomaticLocation(cancellationVersion)) {
      return null;
    }

    return { ...location, cancellationVersion };
  }

  canApplyAutomaticLocation(location: AutomaticLocationContext): boolean {
    return this.addressService.canApplyAutomaticLocation(location.cancellationVersion);
  }

  async applyHomepageLocation(location: AutomaticLocationContext): Promise<void> {
    if (!this.canApplyAutomaticLocation(location)) {
      return;
    }

    this.channelService.channelReq.longitude = location.longitude;
    this.channelService.channelReq.latitude = location.latitude;

    // Automatic homepage location only needs coordinates, not a resolved address.
    await this.channelService.getChannel({
      ...this.channelService.channelReq,
      longitude: location.longitude,
      latitude: location.latitude
    });
  }

  async getManualLocationAddress(channelId?: number): Promise<CommonAddressResponse | null> {
    const location = await this.addressService.getUserGpsLocation(true);
    return this.channelService.resolveLocationAddress(location.longitude, location.latitude, channelId);
  }

  selectAddress(address: CommonAddressResponse): void {
    const pathSegments = this.channelService.getSegments();
    this.userService.updateSelectedAddress({
      ...address,
      addressSubscriptionTag: pathSegments[0] ?? null
    });
  }
}
